/* Läuft hier der aktuelle Stand? — gemeinsam für alle Vimaana-Web-Apps.
 *
 * Die Apps sind PWAs. Ein Service Worker beantwortet Navigationen aus dem
 * Precache, damit sie ohne Netz starten — genau das lässt sie aber auch nach
 * einem Deploy erst einmal weiterlaufen wie bisher. Auf dem iPhone, wo die App
 * vom Home-Bildschirm tage- oder wochenlang im Hintergrund bleibt, kann das
 * lange dauern.
 *
 * Zwei Werte, ein Vergleich:
 *   __VG_BUILD__   der Stand, der GERADE LÄUFT (kommt aus dem Bundle, ist also
 *                  selbst so alt wie der Cache)
 *   version.json   der Stand, der AUF DEM SERVER liegt (frisch geholt)
 *
 * Beides wird beim Bauen vom selben Plugin gesetzt:
 * `packages/vimaana-ui/vite/buildstempel.mjs`.
 */

export type Buildstempel = {
  app: string;
  /** ISO-Zeitpunkt des Builds. */
  gebaut: string;
  /** Kurzes Git-Kürzel, leer wenn beim Bauen kein Git erreichbar war. */
  commit: string;
};

declare global {
  // Wird vom Vite-Plugin eingesetzt. Fehlt es (DevServer ohne Plugin, Tests),
  // greift der Fallback unten — die Prüfung schweigt dann, statt zu lügen.
  const __VG_BUILD__: Buildstempel | undefined;
}

const LEER: Buildstempel = { app: "", gebaut: "", commit: "" };

/** Der Stand, der in diesem Browser gerade läuft. */
export function laufenderBuild(): Buildstempel {
  return typeof __VG_BUILD__ === "undefined" ? LEER : __VG_BUILD__;
}

/** „18.08.2026, 19:42" — der Stempel, wie ihn ein Mensch liest. */
export function buildText(b: Buildstempel = laufenderBuild()): string {
  if (!b.gebaut) return "Entwicklungsstand";
  const d = new Date(b.gebaut);
  if (Number.isNaN(d.getTime())) return "Entwicklungsstand";
  const datum = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  const zeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  return b.commit ? `${datum}, ${zeit} · ${b.commit}` : `${datum}, ${zeit}`;
}

/** Holt den Serverstand — ungecacht, sonst prüft man den Cache gegen sich selbst. */
export async function serverBuild(): Promise<Buildstempel | null> {
  try {
    const antwort = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!antwort.ok) return null;
    const daten = (await antwort.json()) as Partial<Buildstempel>;
    if (!daten || typeof daten.gebaut !== "string") return null;
    return { app: daten.app || "", gebaut: daten.gebaut, commit: daten.commit || "" };
  } catch {
    // Offline oder DevServer ohne Datei — kein Grund, jemanden zu behelligen.
    return null;
  }
}

/** Ist eine neuere Fassung ausgeliefert als die, die hier läuft? */
export async function neuerStandVerfuegbar(): Promise<boolean> {
  const hier = laufenderBuild();
  // Ohne eigenen Stempel (DevServer) lässt sich nichts vergleichen.
  if (!hier.gebaut) return false;
  const dort = await serverBuild();
  if (!dort) return false;
  // Verglichen wird der Zeitpunkt, nicht das Kürzel: zwei Builds aus demselben
  // Commit sind derselbe Code, und ein zurückgerollter Server soll nicht als
  // „neue Version" erscheinen.
  return new Date(dort.gebaut).getTime() > new Date(hier.gebaut).getTime();
}

/**
 * Holt die aktuelle Fassung wirklich vom Server.
 *
 * Reihenfolge ist wichtig: erst den Service Worker erneuern und den wartenden
 * übernehmen lassen, dann die Caches leeren, dann neu laden. Umgekehrt füllte
 * der alte Worker die eben geleerten Caches sofort wieder.
 *
 * Ein einfaches `location.reload()` genügt danach: Caddy liefert `index.html`
 * mit `no-store` aus, sie kommt also nicht aus dem HTTP-Cache des Browsers.
 */
export async function neueVersionLaden(): Promise<void> {
  // Höchstens so lange wird aufgeräumt — danach wird in jedem Fall neu geladen.
  // Vorher wartete der Knopf auf `registration.update()`; das löst je nach
  // Browser erst nach der Installation des neuen Workers auf (samt Precache)
  // oder gar nicht, und der Balken blieb bei „Lädt…" stehen (Benjamin,
  // 25.09.2026: „bleibt immer hängen").
  const frist = new Promise<void>((fertig) => window.setTimeout(fertig, 2500));
  const aufraeumen = (async () => {
    try {
      if ("serviceWorker" in navigator) {
        // Abmelden statt erneuern: ohne Worker kommt die Seite beim Neuladen
        // sicher vom Server, die App meldet ihren (neuen) Worker danach von
        // selbst wieder an.
        const registrierungen = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrierungen.map((r) => r.unregister().catch(() => false)));
      }
      if (typeof caches !== "undefined") {
        const namen = await caches.keys();
        await Promise.all(namen.map((n) => caches.delete(n)));
      }
    } catch {
      /* Auch wenn das Aufräumen scheitert: neu laden hilft meistens trotzdem. */
    }
  })();
  await Promise.race([aufraeumen, frist]);
  window.location.reload();
}
